// Where the margin's pinned content shows (docs/design/SIVUPALSTA.md "Shell"):
//   ≥ 1180px on a page with a spread → lane B (Spread.tsx);
//   ≥ 900px otherwise                → a 380px side panel square against the right edge;
//   phone                            → the word sheet / the settings sheet (~55% high).
// The content itself (the word digest, quick settings) is a lazy chunk each: a page full of word
// links loads neither until one is opened.
import { lazy, Suspense } from 'react';
import { Icon } from '../components/Icon';
import { Sheet } from '../components/Sheet';
import { useLang, useT } from '../i18n';
import { termById, termTitle } from '../grammar/terms';
// a term's card is small and its texts are loaded already: no chunk of its own (no loading line)
import { TermCard } from '../grammar/terms/TermCard';
import { useMedia } from '../lib/media';
import { VolumeControl } from '../recordings/VolumeControl';
import { useMargin } from './context';

const WordDigest = lazy(() => import('./WordDigest').then((m) => ({ default: m.WordDigest })));
const QuickSettings = lazy(() => import('./QuickSettings').then((m) => ({ default: m.QuickSettings })));

/** The PC widths of the spread (tokens.css; margin.css uses the same values). */
export const WIDE = '(min-width: 1180px)';
export const WIDEST = '(min-width: 1600px)';

export type MarginHostKind = 'lane' | 'panel' | 'sheet';

export function useMarginHost(): MarginHostKind {
  const wide = useMedia(WIDE);
  const desk = useMedia('(min-width: 900px)');
  const { spread } = useMargin();
  return wide && spread ? 'lane' : desk ? 'panel' : 'sheet';
}

/** The pinned part's title (the sheet's, or the lane's pinned head). */
export function usePinnedTitle(): string {
  const t = useT();
  const lang = useLang();
  const { quick, word, term, lineInfo } = useMargin();
  const def = term ? termById(term) : undefined;
  if (!quick && !word && term) return def ? termTitle(def, lang) : t.grammarTerms.title;
  return quick ? t.margin.quick.title : word ? t.margin.word.title : (lineInfo?.title ?? t.margin.lane);
}

/** What is pinned: quick settings first, then the open word, the open term, then the active line's tool. */
export function PinnedContent() {
  const t = useT();
  const { quick, word, term, lineInfo } = useMargin();
  const loading = <p className="quiet">{t.today.loading}</p>;
  if (quick) {
    return (
      <Suspense fallback={loading}>
        <QuickSettings />
      </Suspense>
    );
  }
  if (word) {
    return (
      <Suspense fallback={loading}>
        <WordDigest wordRef={word} />
      </Suspense>
    );
  }
  if (term) return <TermCard id={term} />;
  return lineInfo?.tool ?? null;
}

/** Lane B's head: the volume slider and "Pika-asetukset" (PC, ≥ 1180px on a spread). */
export function MarginHead() {
  const t = useT();
  const { quick, actions } = useMargin();
  return (
    <div className="lane-head">
      <VolumeControl compact />
      <button
        type="button"
        className="btn btn-small lane-quick"
        aria-pressed={quick}
        onClick={(e) => (quick ? actions?.close('quick') : actions?.openQuick(e.currentTarget))}
      >
        <Icon name="sliders" size={18} />
        <span>{t.margin.quick.open}</span>
      </button>
    </div>
  );
}

/** The button that opens quick settings: the phone top bar (icon), the PC rail foot (with text). */
export function QuickButton({ variant }: { variant: 'topbar' | 'rail' }) {
  const t = useT();
  const { quick, actions } = useMargin();
  const open = (el: HTMLElement) => (quick ? actions?.close('quick') : actions?.openQuick(el));
  if (variant === 'topbar') {
    return (
      <button type="button" className="icon-btn topbar-quick" aria-pressed={quick} aria-label={t.margin.quick.open} onClick={(e) => open(e.currentTarget)}>
        <Icon name="sliders" />
      </button>
    );
  }
  return (
    <button type="button" className="rail-link rail-quick" aria-pressed={quick} onClick={(e) => open(e.currentTarget)}>
      <Icon name="sliders" />
      <span>{t.margin.quick.open}</span>
    </button>
  );
}

/** The sheet / side panel host (the shell renders it; it stays closed while lane B hosts). */
export function MarginSheetHost() {
  const host = useMarginHost();
  const { quick, word, term, anchor, actions } = useMargin();
  const title = usePinnedTitle();
  const open = host !== 'lane' && (quick || !!word || !!term);
  return (
    <Sheet
      open={open}
      onClose={() => actions?.close(quick ? 'quick' : word ? 'word' : 'term')}
      title={title}
      size="half"
      anchor={anchor}
      className={quick ? 'is-quick' : undefined}
    >
      <PinnedContent />
    </Sheet>
  );
}
